import type { ServiceError } from './ServiceError'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical'

interface LogEntry {
  level: LogLevel
  message: string
  timestamp: Date
  context?: Record<string, any>
  error?: Error | ServiceError
  stack?: string
}

/**
 * Centralized logging service for service layer
 */
export class ServiceLogger {
  private static instance: ServiceLogger
  private isDevelopment = import.meta.env.DEV
  private logQueue: LogEntry[] = []
  private maxQueueSize = 100
  
  private constructor() {}
  
  static getInstance(): ServiceLogger {
    if (!ServiceLogger.instance) {
      ServiceLogger.instance = new ServiceLogger()
    }
    return ServiceLogger.instance
  }
  
  /**
   * Log debug information (only in development)
   */
  debug(message: string, context?: Record<string, any>) {
    if (this.isDevelopment) {
      this.log('debug', message, context)
    }
  }
  
  /**
   * Log general information
   */
  info(message: string, context?: Record<string, any>) {
    this.log('info', message, context)
  }
  
  /**
   * Log warnings
   */
  warn(message: string, context?: Record<string, any>) {
    this.log('warn', message, context)
  }
  
  /**
   * Log errors with context
   */
  error(message: string, error?: Error | ServiceError, context?: Record<string, any>) {
    this.log('error', message, context, error)
  }
  
  /**
   * Log critical errors that need immediate attention
   */
  critical(message: string, error?: Error | ServiceError, context?: Record<string, any>) {
    this.log('critical', message, context, error)
    // In production, this could trigger alerts
    if (!this.isDevelopment) {
      this.sendToRemoteLogging({
        level: 'critical',
        message,
        error: error,
        context,
        timestamp: new Date()
      })
    }
  }
  
  /**
   * Core logging method
   */
  private log(
    level: LogLevel,
    message: string,
    context?: Record<string, any>,
    error?: Error | ServiceError
  ) {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date(),
      context,
      error,
      stack: error?.stack
    }
    
    // Add to queue
    this.addToQueue(entry)
    
    // Console output in development
    if (this.isDevelopment) {
      this.consoleLog(entry)
    }
    
    // Send critical errors to remote in production
    if (!this.isDevelopment && (level === 'error' || level === 'critical')) {
      this.sendToRemoteLogging(entry)
    }
  }
  
  /**
   * Add log entry to queue (for debugging)
   */
  private addToQueue(entry: LogEntry) {
    this.logQueue.push(entry)
    if (this.logQueue.length > this.maxQueueSize) {
      this.logQueue.shift()
    }
  }
  
  /**
   * Console logging with proper formatting
   */
  private consoleLog(entry: LogEntry) {
    const prefix = `[${entry.timestamp.toISOString()}] [${entry.level.toUpperCase()}]`
    const style = this.getConsoleStyle(entry.level)
    
    console.log(`%c${prefix}%c ${entry.message}`, style, 'color: inherit')
    
    if (entry.context) {
      console.log('Context:', entry.context)
    }
    
    if (entry.error) {
      console.error('Error:', entry.error)
    }
  }
  
  /**
   * Get console style based on log level
   */
  private getConsoleStyle(level: LogLevel): string {
    const styles: Record<LogLevel, string> = {
      debug: 'color: #6b7280',
      info: 'color: #3b82f6',
      warn: 'color: #f59e0b',
      error: 'color: #ef4444',
      critical: 'color: #dc2626; font-weight: bold'
    }
    return styles[level]
  }
  
  /**
   * Send logs to remote service (placeholder)
   */
  private async sendToRemoteLogging(entry: Partial<LogEntry>) {
    // In production, this would send to a service like Sentry, LogRocket, etc.
    // For now, just store in localStorage as backup
    try {
      const logs = JSON.parse(localStorage.getItem('service_logs') || '[]')
      logs.push({
        ...entry,
        timestamp: entry.timestamp?.toISOString()
      })
      // Keep only last 50 entries
      if (logs.length > 50) {
        logs.splice(0, logs.length - 50)
      }
      localStorage.setItem('service_logs', JSON.stringify(logs))
    } catch {
      // Silently fail if localStorage is full
    }
  }
  
  /**
   * Get recent logs for debugging
   */
  getRecentLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logQueue.filter(entry => entry.level === level)
    }
    return [...this.logQueue]
  }
  
  /**
   * Clear log queue
   */
  clearLogs() {
    this.logQueue = []
  }
}

// Export singleton instance
export const serviceLogger = ServiceLogger.getInstance()